import { NavLink } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Icon } from "@wato/ui";
import { NavIcon } from "../workspace/nav-icons";
import { useBottomNavHidden } from "../../components/support-launcher-visibility";

export interface BottomNavItem {
  readonly to: string;
  readonly label: MessageKey;
  readonly end?: boolean;
  /** Nav-config key (`to`) whose rail icon is drawn above the label. */
  readonly icon?: string;
}

/** WP-125: mobile app-shell bottom nav, shown at ≤768px alongside the existing desktop
 * sidebar (`WorkspaceShell.tsx` renders both; CSS visibility switches between them so the
 * route table and permission filtering stay in one place). Icon-less placeholder — labels
 * only — to keep this WP scoped to the shell itself. */
export function BottomNav({
  items,
  onMore,
  moreOpen = false,
}: {
  /** OWN-219: «More» cell that opens the existing menu sheet (the drawer). */
  onMore?: () => void;
  moreOpen?: boolean;
  items: readonly BottomNavItem[];
}) {
  const { t } = useI18n();
  // An open conversation is a full-screen step (OWN-162): the composer owns the bottom edge.
  const hidden = useBottomNavHidden();
  return (
    <nav
      aria-label={t("dashboard.nav.mobileLabel")}
      data-hidden={hidden ? "true" : undefined}
      className={`fixed inset-x-0 bottom-0 z-10 ${hidden ? "hidden" : "flex"} border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] md:hidden`}
    >
      <ul className="flex w-full">
        {items.map((item) => (
          <li key={item.label} className="min-w-0 flex-1">
            <NavLink
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                isActive
                  ? "flex h-14 flex-col items-center justify-center gap-0.5 px-1 text-xs font-semibold text-on-action-ghost"
                  : "flex h-14 flex-col items-center justify-center gap-0.5 px-1 text-xs text-fg-muted"
              }
            >
              {item.icon !== undefined ? <NavIcon to={item.icon} /> : null}
              <span className="w-full truncate text-center">{t(item.label)}</span>
            </NavLink>
          </li>
        ))}
        {onMore ? (
          <li className="min-w-0 flex-1">
            <button
              type="button"
              data-testid="bottom-nav-more"
              aria-expanded={moreOpen}
              onClick={onMore}
              className={`flex h-14 w-full flex-col items-center justify-center gap-0.5 px-1 text-xs focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus ${moreOpen ? "font-semibold text-on-action-ghost" : "text-fg-muted"}`}
            >
              <Icon name="more" size={20} />
              <span className="w-full truncate text-center">{t("dashboard.nav.sectionMore")}</span>
            </button>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}
